import * as React from 'react';
import { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { getDisplayName } from '@hetivo/hetivo-lib-utils';

import type { NavigationItem } from '../../../types/navigation';
import { AccessDeniedPage } from '../access-denied-page';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarInset, SidebarProvider } from '../../ui/sidebar';
import { DefaultPendingPage } from '../default-pending-page';
import { SidebarCollapseTrigger } from '../sidebar-collapse-trigger';
import { SidebarNav } from '../sidebar-nav';
import { SidebarUserMenu } from '../sidebar-user-menu';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthenticatedLayoutAuth {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether authentication state is still being determined */
  isLoading: boolean;
  /** Sign out callback */
  signOut: () => Promise<void>;
  /** Authenticated user from the auth provider */
  user: null | { email: string; name: string };
}

export interface AuthenticatedLayoutProps {
  /** Auth state from the app's auth provider */
  auth: AuthenticatedLayoutAuth;
  /** The backend user profile (first/last name for display) — resolved in the app to avoid cross-instance context issues */
  backendUser?: { firstName?: string; lastName?: string } | null;
  /** Optional additional nav items rendered in the sidebar footer area (above user menu) */
  footerNavItems?: NavigationItem[];
  /** Permission check function from the app's PermissionsProvider */
  hasPermission: (permission: string) => boolean;
  /** Whether permissions are still being loaded */
  isPermissionsLoading: boolean;
  /** Supported language codes for the language switcher */
  languages: string[];
  /** Logo element to render in the sidebar header */
  logo: React.ReactNode;
  /** Top-level navigation items for the sidebar */
  navItems: NavigationItem[];
  /** The permission string required to access the layout (e.g., 'backoffice:access') */
  requiredPermission: string;
  /** Optional banner rendered above the main content area (e.g., impersonation banner) */
  topBanner?: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AuthenticatedLayout({
  auth,
  backendUser,
  footerNavItems,
  hasPermission,
  isPermissionsLoading,
  languages,
  logo,
  navItems,
  requiredPermission,
  topBanner,
}: AuthenticatedLayoutProps): React.JSX.Element {
  const { isAuthenticated, isLoading, signOut, user } = auth;
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  // Reactive guard: redirect to login when auth state changes to unauthenticated
  // (e.g., after session timeout fires while on an authenticated page).
  // beforeLoad only runs on navigation — this handles in-place auth state changes.
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      void navigate({ search: { redirect: location.pathname }, to: '/login' });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  const handleSignOut = async (): Promise<void> => {
    await signOut();
  };

  if (isLoading || isPermissionsLoading) {
    return <DefaultPendingPage />;
  }

  if (!hasPermission(requiredPermission)) {
    return (
      <AccessDeniedPage
        message={t('auth.accessDenied.message')}
        onSignOut={handleSignOut}
        signOutLabel={t('auth.signOut')}
        title={t('auth.accessDenied.title')}
      />
    );
  }

  const displayName = getDisplayName(backendUser ?? undefined) ?? user?.name;

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader className="min-h-[4.75rem] border-b border-sidebar-border p-4">
          <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
            {logo}
            <SidebarCollapseTrigger />
          </div>
        </SidebarHeader>
        <SidebarContent className="px-3 pt-4">
          <SidebarNav
            hasPermission={hasPermission}
            items={navItems}
            renderLink={({ children, path }) => <Link to={path}>{children}</Link>}
            translate={t}
          />
        </SidebarContent>
        <SidebarFooter className="border-t border-sidebar-border px-3 py-4">
          {footerNavItems && (
            <SidebarNav
              className="mb-2"
              hasPermission={hasPermission}
              items={footerNavItems}
              renderLink={({ children, path }) => <Link to={path}>{children}</Link>}
              translate={t}
            />
          )}
          <SidebarUserMenu
            currentLanguage={i18n.language}
            displayName={displayName}
            email={user?.email}
            languages={languages}
            onLanguageChange={(lang) => void i18n.changeLanguage(lang)}
            onSignOut={handleSignOut}
          />
        </SidebarFooter>
      </Sidebar>
      <SidebarInset className="h-svh overflow-hidden">
        {topBanner}
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
